import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useTopKpis, usePoolsInFlight } from "@/hooks/capitalMarkets/useDashboard";
import { TopKpisRow } from "./dashboard/TopKpisRow";
import {
  PoolsInFlightTable,
  type ConcentrationFilter,
} from "./dashboard/PoolsInFlightTable";
import { PoolsBlockedOnGates } from "./dashboard/PoolsBlockedOnGates";
import { UpcomingEventsList } from "./dashboard/UpcomingEventsList";
import { AggregateConcentrationGrid } from "./dashboard/AggregateConcentrationGrid";
import { CapitalMarketsWatchlist } from "./dashboard/CapitalMarketsWatchlist";
import { CapitalMarketsRecentlyViewed } from "./dashboard/CapitalMarketsRecentlyViewed";
import { useDateFormatter } from "@/utils/datetime";

/**
 * SPV Pool v1.0 §4.7 / PR 9 — the `/capital-markets` cross-pool dashboard.
 * The operator's home base: aggregate KPIs, pools in flight, gate blockers,
 * upcoming events, aggregate concentration, watchlist and recently-viewed.
 */
export function Dashboard() {
  const { user } = useAuth();
  const kpisQuery = useTopKpis();
  const poolsQuery = usePoolsInFlight();
  const [concentrationFilter, setConcentrationFilter] =
    useState<ConcentrationFilter | null>(null);
  const { date } = useDateFormatter();

  const today = date(new Date(), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const pools = poolsQuery.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title="Capital Markets"
        description={
          user
            ? `${user.displayName} · ${today}`
            : today
        }
      />

      <div className="flex flex-col gap-space-4">
        <TopKpisRow kpis={kpisQuery.data} loading={kpisQuery.isLoading} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-space-4">
          <div className="lg:col-span-2 flex flex-col gap-space-4">
            <PoolsInFlightTable
              pools={pools}
              loading={poolsQuery.isLoading}
              error={poolsQuery.isError}
              concentrationFilter={concentrationFilter}
              onClearConcentrationFilter={() => setConcentrationFilter(null)}
            />
            <PoolsBlockedOnGates pools={pools} loading={poolsQuery.isLoading} />
          </div>

          <div className="flex flex-col gap-space-4">
            <UpcomingEventsList />
            <AggregateConcentrationGrid
              onSliceClick={(selection) =>
                setConcentrationFilter({
                  dimensionLabel: selection.dimensionLabel,
                  key: selection.key,
                })
              }
            />
            <CapitalMarketsWatchlist />
            <CapitalMarketsRecentlyViewed />
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
